import React, { useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import SEO from '../components/SEO';
import LandmarkGallery from '../components/LandmarkGallery';
import { Search, Globe, X, MapPin, Layers } from 'lucide-react';
import { collapseByGroups } from '../lib/destinationGroups';

// Ordered region list with icons
const REGION_CONFIG = [
  { key: 'All', label: 'All', icon: Globe },
  { key: 'Global', label: 'Global', icon: Globe },
  { key: 'Asia', label: 'Asia' },
  { key: 'Europe', label: 'Europe' },
  { key: 'North America', label: 'N. America' },
  { key: 'South America', label: 'S. America' },
  { key: 'Africa', label: 'Africa' },
  { key: 'Oceania', label: 'Oceania' },
  { key: 'Caribbean', label: 'Caribbean' },
  { key: 'Middle East', label: 'Middle East' },
];

const AllDestinations = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { allCountries, loadingCountries, destinationGroups, startingPrices, formatPrice } = useApp();
  
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [selectedRegion, setSelectedRegion] = useState('All');

  // Collapse group-member countries into their primary + attach `_group`
  // metadata so the card can open a picker modal instead of navigating.
  const groupedCountries = useMemo(
    () => collapseByGroups(allCountries, destinationGroups, startingPrices),
    [allCountries, destinationGroups, startingPrices],
  );

  // Count per region for badges
  const regionCounts = useMemo(() => {
    const counts = { All: groupedCountries.length };
    groupedCountries.forEach(c => {
      const r = c.region || 'Other';
      counts[r] = (counts[r] || 0) + 1;
    });
    return counts;
  }, [groupedCountries]);

  // Only show regions that actually have countries
  const visibleRegions = useMemo(() => {
    return REGION_CONFIG.filter(r => r.key === 'All' || (regionCounts[r.key] && regionCounts[r.key] > 0));
  }, [regionCounts]);

  // Filter and sort countries
  const filteredCountries = useMemo(() => {
    let result = groupedCountries;

    if (searchQuery.trim()) {
      result = result.filter(c => 
        c.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    if (selectedRegion !== 'All') {
      result = result.filter(c => c.region === selectedRegion);
    }

    return [...result].sort((a, b) => a.name.localeCompare(b.name));
  }, [groupedCountries, searchQuery, selectedRegion]);

  // Group by region when showing "All"
  const groupedByRegion = useMemo(() => {
    if (selectedRegion !== 'All' || searchQuery.trim()) return null;
    
    const groups = {};
    filteredCountries.forEach(country => {
      const region = country.region || 'Other';
      if (!groups[region]) groups[region] = [];
      groups[region].push(country);
    });
    // Sort groups by REGION_CONFIG order
    const ordered = {};
    REGION_CONFIG.forEach(r => {
      if (groups[r.key]) ordered[r.key] = groups[r.key];
    });
    // Add any remaining groups not in REGION_CONFIG
    Object.keys(groups).forEach(key => {
      if (!ordered[key]) ordered[key] = groups[key];
    });
    return ordered;
  }, [filteredCountries, selectedRegion, searchQuery]);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-neutral-950">
      <SEO 
        title="All Destinations - Browse eSIM Plans by Country"
        description="Browse eSIM data plans for 200+ countries and regions. Find affordable travel data for Europe, Asia, Americas, Africa, and Oceania. Instant activation, no contracts."
        keywords="eSIM destinations, travel data plans, international eSIM, Europe eSIM, Asia eSIM, USA eSIM, global data plans, tourist SIM card"
        canonical="/destinations"
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'All Destinations', url: '/destinations' },
        ]}
      />
      <Navbar />
      
      {/* Landmark Photo Gallery Strip */}
      <div className="pt-28">
        <LandmarkGallery />
      </div>

      {/* Hero + Search + Filters (all in one non-sticky section) */}
      <section className="relative pt-8 pb-6 bg-white dark:bg-neutral-900 border-b border-gray-200 dark:border-neutral-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Title */}
          <div className="text-center mb-8">
            <h1 className="text-4xl sm:text-5xl font-black text-gray-900 dark:text-neutral-100 mb-3" data-testid="page-title">All Destinations</h1>
            <p className="text-gray-500 dark:text-neutral-500 text-base sm:text-lg">Browse eSIM plans for 200+ countries and regions</p>
          </div>

          {/* Search Bar */}
          <div className="max-w-2xl mx-auto mb-8">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 dark:text-neutral-500 w-5 h-5" />
              <input
                type="text"
                placeholder="Search countries or regions..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-12 py-3.5 rounded-full border-2 border-gray-200 dark:border-neutral-800 focus:border-black focus:outline-none text-base transition-colors bg-gray-50 dark:bg-neutral-950 focus:bg-white"
                data-testid="destination-search"
              />
              {searchQuery && (
                <button 
                  onClick={() => setSearchQuery('')}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 dark:text-neutral-500 hover:text-gray-600 transition-colors"
                  data-testid="clear-search-btn"
                >
                  <X size={18} />
                </button>
              )}
            </div>
          </div>

          {/* Region Filter Buttons */}
          <div className="flex flex-wrap items-center gap-2" data-testid="region-filters">
            {visibleRegions.map(({ key, label }) => {
              const isActive = selectedRegion === key;
              return (
                <button
                  key={key}
                  onClick={() => setSelectedRegion(key)}
                  className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-semibold transition-all duration-200 ${
                    isActive 
                      ? 'bg-black text-white shadow-md' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-900'
                  }`}
                  data-testid={`filter-region-${key}`}
                >
                  {label}
                  <span className={`ml-1.5 text-xs ${isActive ? 'text-gray-300' : 'text-gray-400'}`}>
                    {regionCounts[key] || 0}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Results */}
      <section className="py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Results Count */}
          <div className="flex items-center justify-between mb-6">
            <p className="text-sm text-gray-500 dark:text-neutral-500" data-testid="results-count">
              <span className="font-bold text-gray-900 dark:text-neutral-100">{filteredCountries.length}</span> destinations found
              {selectedRegion !== 'All' && (
                <button 
                  onClick={() => setSelectedRegion('All')}
                  className="ml-3 text-xs font-semibold text-black dark:text-white underline underline-offset-2 hover:no-underline"
                  data-testid="clear-region-filter"
                >
                  Clear filter
                </button>
              )}
            </p>
          </div>

          {loadingCountries ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
              {Array(20).fill(0).map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="aspect-[4/3] bg-gray-200 dark:bg-neutral-700 rounded-xl" />
                  <div className="mt-3 h-4 bg-gray-200 dark:bg-neutral-700 rounded w-3/4" />
                  <div className="mt-2 h-3 bg-gray-200 dark:bg-neutral-700 rounded w-1/2" />
                </div>
              ))}
            </div>
          ) : filteredCountries.length === 0 ? (
            <div className="text-center py-20" data-testid="no-results">
              <Globe className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-900 dark:text-neutral-100 mb-2">No destinations found</h3>
              <p className="text-gray-500 dark:text-neutral-500 mb-6">Try adjusting your search or filters</p>
              <button 
                onClick={() => { setSearchQuery(''); setSelectedRegion('All'); }}
                className="px-6 py-3 bg-black text-white rounded-full font-bold hover:bg-gray-800 transition-colors"
                data-testid="clear-all-filters-btn"
              >
                Clear Filters
              </button>
            </div>
          ) : groupedByRegion ? (
            // Grouped View (when "All" selected and no search)
            Object.entries(groupedByRegion).map(([region, countries]) => (
              <div key={region} className="mb-10" data-testid={`region-group-${region}`}>
                <div className="flex items-center gap-2 mb-5">
                  <MapPin className="w-5 h-5 text-gray-400 dark:text-neutral-500" />
                  <h2 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-neutral-100">{region}</h2>
                  <span className="text-xs font-medium text-gray-400 dark:text-neutral-500 bg-gray-100 dark:bg-neutral-800 px-2 py-0.5 rounded-full">{countries.length}</span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                  {countries.map(country => (
                    <CountryCard key={country.id} country={country} navigate={navigate} formatPrice={formatPrice} startingPrices={startingPrices} />
                  ))}
                </div>
              </div>
            ))
          ) : (
            // Flat View (when specific region selected or searching)
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
              {filteredCountries.map(country => (
                <CountryCard key={country.id} country={country} navigate={navigate} formatPrice={formatPrice} startingPrices={startingPrices} />
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

// Country Card Component — tapping always navigates to the destination
// detail page. If that destination is a group, the detail page itself
// opens the picker modal.
const CountryCard = ({ country, navigate, formatPrice, startingPrices }) => (
  <button
    onClick={() => navigate(`/destinations/${country.id}`)}
    className="group text-left bg-white dark:bg-neutral-900 rounded-xl overflow-hidden border border-gray-100 dark:border-neutral-800 hover:border-gray-300 hover:shadow-lg transition-all"
    data-testid={`country-card-${country.id}`}
  >
    <div className="relative aspect-[4/3] overflow-hidden">
      <img
        src={country.image || 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&w=600&q=80'}
        alt={country.name}
        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
        onError={(e) => { e.currentTarget.src = 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&w=600&q=80'; }}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
      <div className="absolute top-2 left-2 flex flex-wrap gap-1.5 max-w-[calc(100%-1rem)]">
        {country.isRegion && (
          <div className="bg-white/90 backdrop-blur-sm px-2 py-1 rounded-full">
            <span className="text-xs font-bold text-gray-900 dark:text-gray-900 flex items-center gap-1">
              <Globe size={12} /> Regional
            </span>
          </div>
        )}
        {country._group && (
          <div className="bg-black/80 backdrop-blur-sm px-2 py-1 rounded-full">
            <span className="text-xs font-bold text-white flex items-center gap-1">
              <Layers size={12} /> {(country._group.members || []).length} plans
            </span>
          </div>
        )}
        {(country.tags || []).map((t) => (
          <div key={t} className="bg-emerald-500/95 backdrop-blur-sm px-2 py-1 rounded-full" data-testid={`dest-tag-${country.id}-${t}`}>
            <span className="text-xs font-bold text-white">{t}</span>
          </div>
        ))}
      </div>
    </div>
    <div className="p-4">
      <div className="flex items-center gap-2">
        {/* Earth icon for regional plans, flag for local countries */}
        {country.isRegion ? (
          <div className="w-6 h-4 bg-gradient-to-br from-blue-500 to-emerald-500 rounded shadow flex items-center justify-center">
            <Globe size={12} className="text-white" />
          </div>
        ) : (
          <img 
            src={country.flag} 
            alt={`${country.name} flag`} 
            className="w-6 h-4 rounded shadow object-cover" 
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
        )}
        <h3 className="font-bold text-gray-900 dark:text-neutral-100 truncate">{country.name}</h3>
      </div>
      {(() => {
        const price = country._group
          ? country._groupMinPrice
          : (startingPrices?.[country.id] ?? null);
        return typeof price === 'number' && price > 0 && formatPrice ? (
          <p className="text-xs text-gray-500 dark:text-neutral-400 mt-1" data-testid={`country-card-from-${country.id}`}>
            from <span className="font-bold text-gray-900 dark:text-neutral-100">{formatPrice(price)}</span>
          </p>
        ) : null;
      })()}
    </div>
  </button>
);

export default AllDestinations;
